import React, { useState, useEffect } from 'react';
import { supabase } from '../../../helpers/Supabase';
import { useHistory } from 'react-router-dom';
import Loader from '../../Loader/Loader';
import '../../../styles/components/ChatbotSetup.scss';

const ALLOWED_TYPES = ['text/csv', 'text/plain', 'application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];

const ChatbotSetup: React.FC = () => {
  const [stores, setStores] = useState<any[]>([]);
  const [selectedStore, setSelectedStore] = useState<string>('');
  const [chatbotLink, setChatbotLink] = useState<string | null>(null);
  const [flowType, setFlowType] = useState<'check-in' | 'survey'>('survey');
  const [industry, setIndustry] = useState<string>('');
  const [industryOptions, setIndustryOptions] = useState<any[]>([]);
  const [clientIndustry, setClientIndustry] = useState<string>('');
  const [prompt, setPrompt] = useState('');
  const [instructions, setInstructions] = useState('');
  const [followUpLogic, setFollowUpLogic] = useState({
    positive: { prompt: 'Would you like to leave this review on Google or Yelp?' },
    negative: { prompt: 'We\'re sorry to hear that. Can you tell us more?' },
  });
  const [followUpLinks, setFollowUpLinks] = useState({
    google: '',
    yelp: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [iframeKey, setIframeKey] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [files, setFiles] = useState<FileList | null>(null);
  const [existingFiles, setExistingFiles] = useState<any[]>([]);
  const history = useHistory();

  useEffect(() => {
    const fetchUserAndStores = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        history.push('/');
        return;
      }

      const { data: userRows } = await supabase
        .from('users')
        .select('client_id')
        .eq('auth_user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);

      const userRow = userRows && userRows.length > 0 ? userRows[0] : null;

      if (userRow?.client_id) {
        // Fetch client info to get industry
        const { data: clientData } = await supabase
          .from('clients')
          .select('industry')
          .eq('id', userRow.client_id)
          .single();

        if (clientData?.industry) {
          setClientIndustry(clientData.industry);
          setIndustry(clientData.industry); // Auto-select the client's industry
          // Auto-fetch the template for the client's industry
          fetchIndustryTemplate(clientData.industry);
        }

        const { data: storesData } = await supabase
          .from('stores')
          .select('*')
          .eq('client_id', userRow.client_id)
          .eq('is_active', true);

        setStores(storesData || []);

        if (storesData && storesData.length > 0) {
          const firstStore = storesData[0];
          setSelectedStore(firstStore.id);
          if (firstStore.hash_id) {
            setChatbotLink(`https://app.vivvy.ai/chatbot/${firstStore.hash_id}`);
          }
          // Load existing files for the first store
          fetchStoreFiles(firstStore.id);
        }
      }

      setIsLoading(false);
    };

    fetchUserAndStores();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchIndustryTemplate is stable
  }, [history]);

  useEffect(() => {
    const fetchExistingFlow = async () => {
      if (!selectedStore) return;

      setPrompt('');
      setInstructions('');
      setFollowUpLogic({ positive: { prompt: '' }, negative: { prompt: '' } });
      setFollowUpLinks({ google: '', yelp: '' });

      const { data: existingFlow } = await supabase
        .from('chatbot_flows')
        .select('*')
        .eq('store_id', selectedStore)
        .eq('flow_type', flowType)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (existingFlow) {
        setPrompt(existingFlow.greeting || '');
        setInstructions(existingFlow.gpt_instructions || '');
        if (existingFlow.follow_up_logic) {
          setFollowUpLogic({
            positive: { prompt: existingFlow.follow_up_logic?.positive?.prompt || '' },
            negative: { prompt: existingFlow.follow_up_logic?.negative?.prompt || '' },
          });
          setFollowUpLinks({
            google: existingFlow.follow_up_logic?.links?.google || '',
            yelp: existingFlow.follow_up_logic?.links?.yelp || '',
          });
        }
      }
    };

    if (!isLoading && selectedStore) {
      fetchExistingFlow();
    }
  }, [selectedStore, flowType, isLoading]);

  useEffect(() => {
    const fetchIndustryOptions = async () => {
      const { data } = await supabase
        .from('industry_templates')
        .select('industry')
        .neq('industry', '')
        .order('industry', { ascending: true });
      if (data) {
        const industrySet = new Set<string>();
        data.forEach(d => industrySet.add(d.industry));
        const uniqueIndustries = Array.from(industrySet);
        setIndustryOptions(uniqueIndustries);
      }
    };
    fetchIndustryOptions();
  }, []);

  const fetchIndustryTemplate = async (industry: string) => {
    const { data } = await supabase
      .from('industry_templates')
      .select('*')
      .eq('industry', industry)
      .eq('flow_type', flowType)
      .maybeSingle();

    if (data) {
      setPrompt(data.greeting || '');
      setInstructions(data.gpt_instructions || '');
      setFollowUpLogic({
        positive: { prompt: data.positive_prompt || '' },
        negative: { prompt: data.negative_prompt || '' },
      });
    }
  };

    const handleSave = async () => {
    if (!selectedStore || !prompt || !instructions) {
      alert('All fields are required.');
      return;
    }

    // Upload files first
    await uploadFiles(selectedStore, files);
  
    let finalInstructions = instructions.trim();
  
    const { positive, negative } = followUpLogic;
    const { google, yelp } = followUpLinks;
  
    // Dynamically build contextual instruction additions
    const followUpSnippets: string[] = [];
  
    if (positive.prompt) {
      followUpSnippets.push(`If the customer is happy, say: "${positive.prompt}"`);
    }
  
    if (negative.prompt) {
      followUpSnippets.push(`If the customer is unhappy, say: "${negative.prompt}"`);
    }
  
    if (google) {
      followUpSnippets.push(`If applicable, share this Google review link: ${google}`);
    }
  
    if (yelp) {
      followUpSnippets.push(`Optionally include this Yelp review link: ${yelp}`);
    }
  
    // Merge the snippets if any exist
    if (followUpSnippets.length > 0) {
      finalInstructions += '\n\n' + followUpSnippets.join('\n');
    }
  
    const { error } = await supabase
      .from('chatbot_flows')
      .upsert([{
        store_id: selectedStore,
        flow_type: flowType,
        greeting: prompt,
        gpt_instructions: finalInstructions,
        follow_up_logic: { ...followUpLogic, links: followUpLinks },
      }], {
        onConflict: 'store_id,flow_type'
      });
  
    if (error) {
      console.error(error);
      alert('Error saving config');
    } else {
      console.log('Setting success message to true');
      setShowSuccessMessage(true);
      setIframeKey(prevKey => prevKey + 1);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        console.log('Hiding success message');
        setShowSuccessMessage(false);
      }, 3000);
    }
  };
  

  const fetchStoreFiles = async (storeId: string) => {
    const { data, error } = await supabase
      .from('store_files')
      .select('*')
      .eq('store_id', storeId);
    if (!error) setExistingFiles(data || []);
  };

  const deleteFile = async (filePath: string, id: string) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;
    await supabase.storage.from('store-data').remove([filePath]);
    await supabase.from('store_files').delete().eq('id', id);
    setExistingFiles((prev) => prev.filter(f => f.id !== id));
  };

  const uploadFiles = async (storeId: string, fileList: FileList | null) => {
    if (!fileList) return;

    for (const file of Array.from(fileList)) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        alert(`File type not allowed: ${file.name}`);
        continue;
      }
      
      const path = `${storeId}/${file.name}`;
      
      try {
        // Get the file as a blob
        const fileBlob = new Blob([file], { type: file.type });
        
        // Get the storage URL and access token
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          console.error('No session found');
          continue;
        }

        const storageUrl = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/store-data/${path}`;
        
        // Upload using direct fetch with proper headers
        const response = await fetch(storageUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': file.type,
            'Cache-Control': '3600',
            'x-upsert': 'true'
          },
          body: fileBlob
        });

        if (response.ok) {
          // Add to database
          await supabase.from('store_files').insert({
            store_id: storeId,
            file_path: path,
            file_type: file.type,
          });
          console.log(`Successfully uploaded: ${file.name}`);
        } else {
          console.error(`Upload failed for ${file.name}:`, response.statusText);
        }
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
      }
    }
    
    // Refresh the file list
    fetchStoreFiles(storeId);
  };

  const handleStoreChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const storeId = e.target.value;
    setSelectedStore(storeId);
    const selected = stores.find(s => s.id === storeId);
    if (selected?.hash_id) {
      setChatbotLink(`https://app.vivvy.ai/chatbot/${selected.hash_id}`);
    } else {
      setChatbotLink(null);
    }
    // Load files for the selected store
    fetchStoreFiles(storeId);
  };

  if (isLoading) return <Loader />;

  console.log('Current showSuccessMessage state:', showSuccessMessage);

  return (
    <div className="chatbot-setup">
      <h1>Vivvy Chatbot Setup</h1>
      <div className="setup-container">
        <div className="chatbot-form-container">
        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="form-grid">

  <div className="form-cell">
    <label>Which store is this for?
      <select value={selectedStore} onChange={handleStoreChange}>
        {stores.map(store => (
          <option key={store.id} value={store.id}>{store.store_name}</option>
        ))}
      </select>
      <div className="helper-text">This will connect the chatbot to the correct store page.</div>
    </label>
  </div>

  <div className="form-cell">
    <label>Choose experience type
      <select value={flowType} onChange={(e) => setFlowType(e.target.value as any)}>
        <option value="check-in">Check-In</option>
        <option value="survey">Survey</option>
      </select>
      <div className="helper-text">Decide if this flow is for entering or feedback.</div>
    </label>
  </div>

  <div className="form-cell">
    <label>What type of business is this?
      <select value={industry} onChange={(e) => {
        setIndustry(e.target.value);
        fetchIndustryTemplate(e.target.value);
      }}>
        <option value="">Select an industry...</option>
        {industryOptions.map(ind => (
          <option key={ind} value={ind}>{ind}</option>
        ))}
      </select>
      <div className="helper-text">
        {clientIndustry && industry === clientIndustry 
          ? `Auto-selected from your business profile (${clientIndustry}). You can change this if needed.`
          : 'This helps auto-fill chatbot behavior for your niche.'
        }
      </div>
    </label>
  </div>

  <div className="form-cell">
    <label>What should I say to new visitors?
      <input type="text" value={prompt} onChange={(e) => setPrompt(e.target.value)} />
      <div className="helper-text">This is the chatbot's first message to customers.</div>
    </label>
  </div>

  <div className="form-cell full-span">
    <label>How should I, your chatbot, behave?
      <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} />
      <div className="helper-text">Tell the AI how to respond, tone of voice, etc.</div>
    </label>
  </div>

  <div className="form-cell">
    <label>If they're happy, I'll ask this…
      <input
        type="text"
        value={followUpLogic.positive.prompt}
        onChange={(e) =>
          setFollowUpLogic(prev => ({
            ...prev,
            positive: { ...prev.positive, prompt: e.target.value },
          }))
        }
      />
      <div className="helper-text">Prompt to leave a public review.</div>
    </label>
  </div>

  <div className="form-cell">
    <label>If they're upset, I'll say this…
      <input
        type="text"
        value={followUpLogic.negative.prompt}
        onChange={(e) =>
          setFollowUpLogic(prev => ({
            ...prev,
            negative: { ...prev.negative, prompt: e.target.value },
          }))
        }
      />
      <div className="helper-text">Prompt for more feedback privately.</div>
    </label>
  </div>

  <div className="form-cell">
    <label>Google review link
      <input
        type="text"
        value={followUpLinks.google}
        onChange={(e) =>
          setFollowUpLinks(prev => ({ ...prev, google: e.target.value }))
        }
      />
      <div className="helper-text">Paste your Google review page URL.</div>
    </label>
  </div>

  <div className="form-cell">
    <label>Yelp review link
      <input
        type="text"
        value={followUpLinks.yelp}
        onChange={(e) =>
          setFollowUpLinks(prev => ({ ...prev, yelp: e.target.value }))
        }
      />
      <div className="helper-text">Optional Yelp link if you want both.</div>
    </label>
  </div>

  <div className="form-cell full-span">
    <label htmlFor="chatbot-files">📁 Upload Store Data (PDF/CSV/Excel):
      <input 
        id="chatbot-files"
        type="file" 
        multiple 
        onChange={(e) => setFiles(e.target.files)}
        aria-label="Upload store data files for chatbot training"
      />
      <div className="helper-text">Upload documents to help train your chatbot about your business.</div>
    </label>
  </div>

  {/* Existing Files Section */}
  {existingFiles.length > 0 && (
    <div className="form-cell full-span">
      <h4>📁 Existing Files</h4>
      <ul className="file-list">
        {existingFiles.map(file => (
          <li key={file.id} className="file-row">
            <a href={`https://xoulgvjyxtwmbndzbgbi.supabase.co/storage/v1/object/public/store-data/${file.file_path}`} target="_blank" rel="noreferrer">
              📄 {file.file_path.split('/').pop()}
            </a>
            <button 
              type="button"
              onClick={() => deleteFile(file.file_path, file.id)}
              className="delete-file-btn"
            >
              🗑 Delete
            </button>
          </li>
        ))}
      </ul>
    </div>
  )}

  <div className="form-footer full-span">
    <button type="submit">💾 Save Chatbot Flow</button>
  </div>
</form>

        </div>

        {chatbotLink && (
          <div className="preview-container">
            <div className="phone-mockup">
              <div className="phone-header"></div>
              <iframe
                key={iframeKey}
                src={chatbotLink}
                title="Chatbot Preview"
                className="chatbot-iframe"
                allow="clipboard-write"
              ></iframe>
            </div>
          </div>
        )}
      </div>

      {/* Success Message Popup */}
      {showSuccessMessage && (
        <div className="success-popup-overlay" onClick={() => setShowSuccessMessage(false)}>
          <div className="success-popup" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">✨</div>
            <h3>Design Saved Successfully!</h3>
            <p>Your chatbot configuration has been updated and is ready to use.</p>
            <div className="success-sparkles">
              <span>✨</span>
              <span>💫</span>
              <span>⭐</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotSetup;